/**
 * Parry timing arbitration.
 *
 * The client reports *when the player started making noise* (Web Audio onset),
 * not when the speech recogniser finally returned a string. That timestamp is
 * in the client's clock, so the server converts it with the measured offset and
 * then refuses anything outside a symmetric, published window. Both players get
 * the same numbers regardless of ping.
 */
import { TIMING, clamp } from '@spell/shared-types';

export interface TimestampCheck {
  ok: boolean;
  /** Server-clock time the parry attempt is credited to. */
  serverTime: number;
  reason?: 'too-old' | 'too-new';
}

/**
 * Validate a client-submitted onset timestamp.
 *
 * @param clientOnset  performance-clock timestamp reported by the client
 * @param clockOffset  serverNow - clientNow, measured by the ping handshake
 * @param serverNow    current server time
 * @param rttMs        measured round trip, used to widen the allowance fairly
 */
export function validateOnsetTimestamp(
  clientOnset: number,
  clockOffset: number,
  serverNow: number,
  rttMs: number,
): TimestampCheck {
  const mapped = clientOnset + clockOffset;
  const backdateAllowance =
    TIMING.MAX_TIMESTAMP_BACKDATE_MS + clamp(rttMs, 0, 400);
  const age = serverNow - mapped;
  if (age > backdateAllowance) {
    return { ok: false, serverTime: serverNow - backdateAllowance, reason: 'too-old' };
  }
  if (age < -TIMING.MAX_TIMESTAMP_FUTURE_MS) {
    return { ok: false, serverTime: serverNow, reason: 'too-new' };
  }
  // Never let a client claim a time in the future.
  return { ok: true, serverTime: Math.min(mapped, serverNow) };
}

/**
 * How long a parryable projectile must hang at the point of impact before it
 * resolves.
 *
 * `resolveParryWindow` credits a parry to the moment the defender started
 * speaking, but the evidence for it only reaches the server once the sigil is
 * finished and the key released — well after the onset. A projectile that
 * despawned on contact was therefore no longer a candidate by the time a
 * perfectly-timed parry arrived, which is also why the trailing grace below
 * could never fire. The hold is what makes the published window real.
 *
 * It is the trailing grace plus the defender's own round trip, capped: an
 * inflated ping buys a slightly longer beat and nothing more, because the
 * onset is still judged against `impactAt`.
 */
export function parryResolveHoldMs(rttMs: number): number {
  return (
    TIMING.PARRY_RESOLVE_HOLD_MS + clamp(rttMs, 0, TIMING.PARRY_RESOLVE_RTT_CAP_MS)
  );
}

export interface ParryCandidate {
  id: string;
  /** Server-clock time the projectile is predicted to reach the defender. */
  impactAt: number;
  parryable: boolean;
}

export interface ParryWindowResult {
  hit: ParryCandidate | null;
  /** Signed error in ms: negative = early, positive = late. */
  error: number;
  /** 0..1 quality of the timing alone. */
  timingScore: number;
}

/**
 * Pick which incoming projectile a parry attempt applies to.
 *
 * A parry is valid from `PARRY_WINDOW_MS` before impact until
 * `PARRY_LATENCY_GRACE_MS` after it — the trailing slack exists so a laggy
 * defender is not punished for the network, and it is identical for both
 * players.
 */
export function resolveParryWindow(
  attemptAt: number,
  candidates: ParryCandidate[],
  extraGraceMs = 0,
): ParryWindowResult {
  const early = TIMING.PARRY_WINDOW_MS + extraGraceMs;
  const late = TIMING.PARRY_LATENCY_GRACE_MS + extraGraceMs;

  let best: ParryCandidate | null = null;
  let bestError = Number.POSITIVE_INFINITY;

  for (const c of candidates) {
    if (!c.parryable) continue;
    const error = attemptAt - c.impactAt; // negative = parried early
    if (error < -early || error > late) continue;
    if (Math.abs(error) < Math.abs(bestError)) {
      best = c;
      bestError = error;
    }
  }

  if (!best) return { hit: null, error: Number.POSITIVE_INFINITY, timingScore: 0 };

  // Perfect timing sits just before impact; earlier is progressively worse.
  const normalized = bestError <= 0 ? -bestError / early : bestError / Math.max(late, 1);
  const timingScore = clamp(1 - normalized * 0.55, 0, 1);
  return { hit: best, error: Math.round(bestError), timingScore };
}
